import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import moment from 'moment';
import { useToasts } from 'react-toast-notifications';
import { useTranslation } from 'react-i18next';
import * as ROUTES from '../../../constants/routes';
import { blogService } from '../../../services/blogService';

export default function BlogList() {
  const [blogPosts, setBlogPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const { addToast } = useToasts();
  const { t } = useTranslation();

  useEffect(() => {
    let isMounted = true;
    const fetchPosts = async () => {
      try {
        const { posts, cursor: nextCursor, hasMore: more } =
          await blogService.getPostsPage({ pageSize: 10 });
        if (!isMounted) {
          return;
        }
        setBlogPosts(posts);
        setCursor(nextCursor);
        setHasMore(more);
      } catch (error) {
        if (isMounted) {
          addToast(t('blog.loadError'), {
            appearance: 'error',
            autoDismiss: true,
          });
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchPosts();

    return () => {
      isMounted = false;
    };
  }, [addToast, t]);

  const handleLoadMore = async () => {
    if (!hasMore || loadingMore) {
      return;
    }
    setLoadingMore(true);
    try {
      const { posts, cursor: nextCursor, hasMore: more } =
        await blogService.getPostsPage({ pageSize: 10, cursor });
      setBlogPosts(prev => [...prev, ...posts]);
      setCursor(nextCursor);
      setHasMore(more);
    } catch (error) {
      addToast(t('blog.loadMoreError'), {
        appearance: 'error',
        autoDismiss: true,
      });
    } finally {
      setLoadingMore(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm(t('common.confirmDelete'))) {
      try {
        await blogService.deletePost(id);
        setBlogPosts(blogPosts.filter(post => post.id !== id));
        addToast(t('blog.deleteSuccess'), {
          appearance: 'success',
          autoDismiss: true,
        });
      } catch (error) {
        addToast(t('blog.deleteError'), {
          appearance: 'error',
          autoDismiss: true,
        });
      }
    }
  };

  if (loading) {
    return (
      <div className="container text-center">
        <div className="spinner-border" role="status">
          <span className="sr-only">{t('common.loading')}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="row mb-4">
        <div className="col">
          <h2>{t('blog.listTitle')}</h2>
        </div>
        <div className="col-auto">
          <Link
            to={ROUTES.ADMIN_BLOG_CREATE}
            className="btn btn-primary"
          >
            {t('blog.addNewPost')}
          </Link>
        </div>
      </div>
      
      {blogPosts.length === 0 ? (
        <div className="alert alert-info">
          {t('blog.noPosts')}
        </div>
      ) : (
        <>
          <div className="table-responsive">
            <table className="table table-striped">
              <thead>
                <tr>
                  <th>{t('blog.columns.title')}</th>
                  <th>{t('blog.columns.date')}</th>
                  <th>{t('blog.columns.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {blogPosts.map(post => (
                  <tr key={post.id}>
                    <td>{post.title}</td>
                    <td>
                      {post.createdAt ? moment(post.createdAt.toDate()).format('DD.MM.YYYY') : t('common.noDate')}
                    </td>
                    <td>
                      <div className="btn-group">
                        <Link
                          to={`${ROUTES.ADMIN_BLOG_EDIT}/${post.id}`}
                          className="btn btn-sm btn-outline-primary"
                        >
                          {t('blog.edit')}
                        </Link>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger ml-2"
                          onClick={() => handleDelete(post.id)}
                        >
                          {t('blog.delete')}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {hasMore && (
            <div className="text-center my-3">
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleLoadMore}
                disabled={loadingMore}
              >
                {loadingMore ? (
                  <span
                    className="spinner-grow spinner-grow-sm"
                    role="status"
                    aria-hidden="true"
                  ></span>
                ) : (
                  t('blog.loadMore')
                )}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
} 